import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Avatar,
  Grid,
  Snackbar,
  Alert,
  Autocomplete,
  Tooltip,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockClockIcon from '@mui/icons-material/LockClock';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import UserProfileServices from '../../services/userProfile.services';
import { getInitialsFromEmail } from '../../utils/userUtils';
import logger from '../../utils/logger';
import styles from './UserProfile.module.css';
import { cityTimezoneOptions } from '../../utils/usersProfileLocation';
import PhotoGallery from './PhotoGallery';

const EditUserProfile = ({ 
  open, 
  onClose, 
  userProfile, 
  onSave,
  userInitial,
  profilePicture,
  setProfilePicture,
  onNotification,
  onRefreshProfile
}) => {
  const [preferredName, setPreferredName] = useState(userProfile?.preferredName || '');
  const [birthday, setBirthday] = useState(userProfile?.birthday || '');
  const [statusMessage, setStatusMessage] = useState(userProfile?.statusMessage || '');
  const [timezone, setTimezone] = useState(userProfile?.timezone || '');
  const [city, setCity] = useState(userProfile?.city || '');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  
  const fileInputRef = useRef(null);
  
  // Reset form fields when userProfile changes (including after successful saves)
  React.useEffect(() => {
    if (userProfile) {
      setPreferredName(userProfile.preferredName || '');
      setBirthday(userProfile.birthday || '');
      setStatusMessage(userProfile.statusMessage || '');
      setTimezone(userProfile.timezone || '');
      setCity(userProfile.city || '');
    }
  }, [userProfile]);
  
  // Reset states only when modal opens
  React.useEffect(() => {
    if (open) {
      setHasChanges(false);
      setHasSaved(false);
    }
  }, [open]);
  
  React.useEffect(() => {
    if (userProfile) {
      const hasFormChanges = 
        preferredName !== (userProfile.preferredName || '') ||
        birthday !== (userProfile.birthday || '') ||
        statusMessage !== (userProfile.statusMessage || '') ||
        timezone !== (userProfile.timezone || '') ||
        city !== (userProfile.city || '');
      
      setHasChanges(hasFormChanges);
      
      // Reset hasSaved when new changes are made
      if (hasFormChanges) {
        setHasSaved(false);
      }
      
      // Debug logging for button state
      logger.debug('Button state:', { hasChanges: hasFormChanges, hasSaved });
    }
  }, [preferredName, birthday, statusMessage, timezone, city, userProfile, hasSaved]);
  
  // Fetch photo count when component opens
  React.useEffect(() => {
    if (open) {
      fetchPhotoCount();
      
      // Request a profile refresh to ensure we have the latest data
      // This helps prevent issues with stale profile picture key
      if (onRefreshProfile && typeof onRefreshProfile === 'function') {
        onRefreshProfile();
      }
    }
  }, [open, onRefreshProfile]);
  
  const fetchPhotoCount = async () => {
    try {
      const response = await UserProfileServices.getUserPhotos();
      setPhotoCount(response.data?.length || 0);
    } catch (error) {
      logger.error('Error fetching photo count:', error);
    }
  };
  
  const getBirthdayRestrictionInfo = () => {
    if (!userProfile || !userProfile.birthdayUpdatedAt) {
      return { restricted: false, message: '', timeRemaining: 0 };
    }
    
    const now = new Date();
    const lastUpdate = new Date(userProfile.birthdayUpdatedAt);
    
    const restrictionConfig = userProfile.birthdayRestrictionConfig;

    if (!restrictionConfig) {
      return { restricted: false, message: '', timeRemaining: 0 };
    }

    let message = restrictionConfig.message || '';
    if (message && !message.endsWith('.')) {
      message = `${message}.`;
    }

    // Calculate total minutes difference
    const diffInMinutes = Math.floor((now - lastUpdate) / (1000 * 60));
    let totalRemainingMinutes;
    
    if (restrictionConfig.unit === 'minutes') {
      totalRemainingMinutes = restrictionConfig.value - diffInMinutes;
    } else if (restrictionConfig.unit === 'hours') {
      totalRemainingMinutes = (restrictionConfig.value * 60) - diffInMinutes;
    } else {
      totalRemainingMinutes = (restrictionConfig.value * 24 * 60) - diffInMinutes;
    }
    
    // Check if the field is empty and restriction is lifted (user sent empty value previously)
    const isFieldEmptyAndNotRestricted = 
      userProfile.birthday === '' && 
      totalRemainingMinutes <= 0;

    if (totalRemainingMinutes > 0 && !isFieldEmptyAndNotRestricted) {
      // Convert to d h m format
      const days = Math.floor(totalRemainingMinutes / (24 * 60));
      const hours = Math.floor((totalRemainingMinutes % (24 * 60)) / 60);
      const minutes = totalRemainingMinutes % 60;
      
      const formattedTime = [
        days > 0 ? `${days}d` : null,
        hours > 0 ? `${hours}h` : null,
        minutes > 0 ? `${minutes}m` : null
      ].filter(Boolean).join(' ');
      
      return { 
        restricted: true, 
        message: message,
        timeRemaining: formattedTime,
        timeUnit: restrictionConfig.unit,
        timeValue: restrictionConfig.value
      };
    }
    
    return { restricted: false, message: message, timeRemaining: 0 };
  };
  
  const birthdayRestriction = getBirthdayRestrictionInfo();
  
  // Calculate the maximum allowed birth date (14 years ago from today)
  const getMaxAllowedBirthDate = () => {
    const today = new Date();
    const minAge = userProfile?.birthdayRestrictionConfig?.minAge || 14;
    const maxDate = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());
    return maxDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };
  
  // Validate if a birthday meets the minimum age requirement
  const isValidBirthdayAge = (birthdayValue) => {
    if (!birthdayValue) return true; // Allow empty values
    
    const birthDate = new Date(birthdayValue);
    const today = new Date();
    const minAge = userProfile?.birthdayRestrictionConfig?.minAge || 14;
    
    // Calculate age
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= minAge;
  };
  
  const handleCityChange = (event, newValue) => {
    if (newValue) {
      if (typeof newValue === 'object' && newValue !== null) {
        setCity(`${newValue.city}, ${newValue.country}`);
        setTimezone(newValue.timezone);
      } else if (typeof newValue === 'string') {
        setCity(newValue);
      }
    } else {
      setCity('');
    }
  };
  
  // Create timezone options with GMT offset values from the cityTimezoneOptions
const availableTimezones = [...new Set(cityTimezoneOptions.map(option => 
    option.timezone
  ))]
  .map(tzValue => {
    const optionWithTz = cityTimezoneOptions.find(opt => opt.timezone === tzValue);
    // Extract GMT offset from the timezone
    const gmtOffset = optionWithTz.label.match(/\(GMT[+-]\d+(?::\d+)?\)/);
    const gmtInfo = gmtOffset ? gmtOffset[0].replace(/[()]/g, '') : '';
    
    return {
      value: tzValue, 
      label: `${tzValue} (${gmtInfo})`
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label));
  
  const handleTimezoneChange = (event, newValue) => {
    if (newValue && typeof newValue === 'object' && newValue !== null && newValue.value) {
      setTimezone(newValue.value);
    } else {
      // If not a valid option object, revert to previous value or empty
      const currentTimezoneOption = availableTimezones.find(opt => opt.value === timezone);
      if (timezone && !currentTimezoneOption) {
        setTimezone('');
      }
    }
  };
  
  const handleProfilePictureClick = () => {
    setUploadError('');
    fileInputRef.current.click();
  };
  
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check photo limit before processing the file
    try {
      const profileSettings = await UserProfileServices.getProfileSettings();
      const maxPhotos = profileSettings.data?.profilePicture?.maxPhotos || 5;
      
      if (photoCount >= maxPhotos) {
        setUploadError(`You have reached the maximum limit of ${maxPhotos} photos. Please delete some photos before uploading new ones.`);
        if (onNotification) {
          onNotification(`Photo limit reached (${maxPhotos}/${maxPhotos}). Delete some photos first.`, 'warning');
        }
        return;
      }
    } catch (error) {
      logger.error('Error checking photo limits:', error);
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size must be less than 5MB');
      if (onNotification) {
        onNotification('Image size must be less than 5MB', 'error');
      }
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      setUploadError('Only image files are allowed');
      if (onNotification) {
        onNotification('Only image files are allowed', 'error');
      }
      return;
    }
    
    const tempUrl = URL.createObjectURL(file);
    setProfilePicture(tempUrl);
    
    setIsUploading(true);
    setUploadError('');
    
    try {
      // For EditUserProfile, set uploaded photo as active immediately
      const response = await UserProfileServices.uploadProfilePicture(file, true);
      
      const updatedProfile = await UserProfileServices.getCurrentUser();
      if (updatedProfile && updatedProfile.profilePictureUrl) {
        setProfilePicture(updatedProfile.profilePictureUrl);
      }

      // Refresh photo count after successful upload
      await fetchPhotoCount();

      URL.revokeObjectURL(tempUrl);
      
      const successMessage = response.data?.message || 'Profile picture uploaded successfully';
      if (onNotification) {
        onNotification(successMessage, 'success');
      }
    } catch (error) {
      logger.error('Error uploading profile picture:', error);
      setUploadError('Failed to upload profile picture. Please try again.');

      if (userProfile?.profilePictureUrl) {
        setProfilePicture(userProfile.profilePictureUrl);
      } else {
        setProfilePicture(null);
      }
      
      const errorMessage = error.response?.data?.message || 'Failed to upload profile picture';
      if (onNotification) {
        onNotification(errorMessage, 'error');
      }
    } finally {
      setIsUploading(false);
    }
  };
  
  /**
   * Remove the current active profile picture
   * NOTE: This function uses deleteSpecificPhoto() instead of removeProfilePicture()
   * to ensure consistency between EditUserProfile and PhotoGallery components.
   */
  const handleRemoveProfilePicture = async () => {
    setIsUploading(true);
    setUploadError('');
    
    try {
      // First try to get all user photos to find the active one
      let photoKeyToDelete = userProfile?.profilePictureKey;
      
      if (!photoKeyToDelete) {
        // If profilePictureKey is not available, fetch photos and find the active one
        try {
          const photosResponse = await UserProfileServices.getUserPhotos();
          const activePhoto = photosResponse.data?.find(photo => photo.isActive);
          if (activePhoto) {
            photoKeyToDelete = activePhoto.key;
          }
        } catch (error) {
          logger.error('Error fetching photos to determine active photo:', error);
        }
      }
      
      if (!photoKeyToDelete) {
        throw new Error('No active profile picture found to remove');
      }
      
      // Use the same deletion method as PhotoGallery for consistency, but skip auto-replacement
      const response = await UserProfileServices.deleteSpecificPhoto(photoKeyToDelete, true);
      
      // Update the profile picture state
      if (response.newActivePhotoUrl) {
        setProfilePicture(response.newActivePhotoUrl);
      } else {
        setProfilePicture(null);
      }
      
      // Refresh photo count after deletion
      await fetchPhotoCount();
      
      const successMessage = response.message || 'Profile picture removed successfully';
      if (onNotification) {
        onNotification(successMessage, 'success');
      }
    } catch (error) {
      logger.error('Error removing profile picture:', error);
      
      let errorMessage;
      if (error.message === 'No active profile picture found to remove') {
        errorMessage = 'No active profile picture to remove. Try refreshing the page.';
      } else {
        errorMessage = error.response?.data?.message || 'Failed to remove profile picture. Please try again.';
      }
      
      setUploadError(errorMessage);
      
      if (onNotification) {
        onNotification(errorMessage, 'error');
      }
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleSave = async () => {
    // Only show warning message when trying to update with a non-empty value
    if (birthdayRestriction.restricted && birthday !== '' && birthday !== userProfile.birthday) {
      if (onNotification) {
        onNotification(`Birthday can't be changed for ${userProfile?.birthdayRestrictionConfig.value} ${userProfile?.birthdayRestrictionConfig.unit}.`, 'warning');
      }
      setBirthday(userProfile.birthday || '');
      return;
    }
    
    const updatedFields = {
      preferredName,
      birthday,
      statusMessage,
      timezone,
      city
    };
    
    const fieldsToUpdate = {};
    Object.keys(updatedFields).forEach(key => {
      // Only include field if it's actually different
      // For birthday, handle empty strings and null values consistently
      if (key === 'birthday') {
        const currentBirthday = userProfile[key] || '';
        const newBirthday = updatedFields[key] || '';
        if (currentBirthday !== newBirthday) {
          fieldsToUpdate[key] = newBirthday;
        }
      } else if (userProfile[key] !== updatedFields[key]) {
        fieldsToUpdate[key] = updatedFields[key];
      }
    });
    
    if (Object.keys(fieldsToUpdate).length > 0) {
      try {
        await onSave(fieldsToUpdate);
        
        // Mark as saved successfully only after successful save
        setHasSaved(true);
        logger.debug('Profile saved successfully, hasSaved set to true');
        
        if (onNotification) {
          // Show a simple success message for profile updates
          onNotification('Your profile has been updated successfully', 'success');
          
          // Only show birthday removal notification in restricted mode when birthday is actually changed from a value to empty
          if (fieldsToUpdate.birthday !== undefined && 
              fieldsToUpdate.birthday === '' && 
              userProfile.birthday && 
              userProfile.birthday !== '' && 
              userProfile.birthdayRestrictionConfig &&
              birthdayRestriction.restricted) {
            setTimeout(() => {
              onNotification('Birthday has been removed. Field remains restricted per policy.', 'info');
            }, 1000);
          }
          // Show update notification when adding or changing a birthday
          else if (fieldsToUpdate.birthday !== undefined &&
                   fieldsToUpdate.birthday !== '' &&
                   userProfile.birthdayRestrictionConfig) {
            setTimeout(() => {
              if (birthdayRestriction.restricted) {
                onNotification(`Birthday has been updated. You will have to wait ${userProfile.birthdayRestrictionConfig.value} ${userProfile.birthdayRestrictionConfig.unit} to change it again.`, 'info');
              }
            }, 1000);
          }
        }
        
      } catch (error) {
        logger.error('Error saving profile:', error);
        
        if (onNotification) {
          const errorMessage = error.response?.data?.message || 'Failed to save profile. Please try again.';
          onNotification(errorMessage, 'error');
        } else {
          setSnackbarMessage('Failed to save profile. Please try again.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
    } else {
      if (onNotification) {
        onNotification('No changes were made to your profile', 'info');
      } else {
        setSnackbarMessage('No changes were made to your profile');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    }
  };
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    if (hasChanges) {
      // Reset all fields to original values (this is "Discard")
      setPreferredName(userProfile?.preferredName || '');
      setBirthday(userProfile?.birthday || '');
      setStatusMessage(userProfile?.statusMessage || '');
      setTimezone(userProfile?.timezone || '');
      setCity(userProfile?.city || '');
      setHasChanges(false);
    } else {
      // Close modal if no changes (this is "Cancel" or "Close")
      onClose();
    }
  };

  const handleOpenPhotoGallery = () => {
    setPhotoGalleryOpen(true);
  };

  const handleClosePhotoGallery = () => {
    setPhotoGalleryOpen(false);
    // Refresh photo count when gallery closes
    fetchPhotoCount();
  };

  const handlePhotoSelected = (newPhotoUrl) => {
    setProfilePicture(newPhotoUrl);
    // Refresh photo count after photo selection
    fetchPhotoCount();
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: styles.dialogPaper,
      }}
    >
      <DialogTitle className={styles.dialogTitle}>Edit Profile Information</DialogTitle>
      <div className={styles.divider}></div>
      <DialogContent>
        <div className={styles.profileEditContainer}>
          <div className={styles.profilePictureSection}>
            <div 
              className={styles.profilePictureUpload}
              onClick={handleProfilePictureClick}
            >
              {profilePicture ? (
                <Avatar 
                  src={profilePicture} 
                  alt="Profile" 
                  className={styles.largeAvatar} 
                />
              ) : (
                <Avatar className={styles.largeAvatar}>
                  {userInitial || (userProfile?.username ? getInitialsFromEmail(userProfile.username) : 'U')}
                </Avatar>
              )}
              <div className={styles.uploadOverlay}>
                <EditIcon />
              </div>
            </div>
            <Typography variant="caption" align="center" className={styles.profilePictureText}>
              Click to change profile picture
              {photoCount > 0 && (
                <span style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                  {photoCount}/5 photos
                </span>
              )}
            </Typography>
            
            {/* Profile picture controls */}
            <div className={styles.profilePictureControls}>
              {profilePicture && (
                <button 
                  onClick={handleRemoveProfilePicture}
                  className={`${styles.iconButton} ${styles.removeProfilePictureButton}`}
                  title="Remove profile picture"
                >
                  <DeleteIcon fontSize="small" />
                </button>
              )}
              
              <button
                onClick={handleOpenPhotoGallery}
                className={styles.galleryButton}
                title="Manage your photo collection"
              >
                <PhotoLibraryIcon fontSize="small" style={{ marginRight: '4px' }} />
                Manage Photos
              </button>
            </div>
            {uploadError && (
              <Typography variant="caption" color="error" align="center" className={styles.uploadErrorText}>
                {uploadError}
              </Typography>
            )}
            <input
              type="file"
              ref={fileInputRef}
              className={styles.hiddenFileInput}
              accept="image/*"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </div>

          <Grid container spacing={2} className={styles.formGrid}>
            <Grid item xs={12}>                
              <TextField
                label="Email"
                value={userProfile?.username || ''}
                fullWidth
                variant="outlined"
                margin="dense"
                disabled
                className={styles.disabledFormField}
                InputProps={{
                  readOnly: true
                }}
              />
            </Grid>
            <Grid item xs={12}>                
              <TextField
                label="Groups"
                value={userProfile?.groups && userProfile.groups.length > 0 ? userProfile.groups.join(', ') : 'No groups assigned'}
                fullWidth
                variant="outlined"
                margin="dense"
                disabled
                className={styles.disabledFormField}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Contact your admin to update group memberships"
              />
            </Grid>
            {userProfile?.firstName && userProfile?.lastName && (
              <Grid item xs={12}>                  
                <TextField
                  label="Full Name"
                  value={`${userProfile.firstName} ${userProfile.lastName}`}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  disabled
                  className={styles.disabledFormField}
                  InputProps={{
                    readOnly: true
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>                
              <TextField
                label="Preferred Name"
                value={preferredName}
                onChange={(e) => setPreferredName(e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
                placeholder="How should we call you?"
                inputProps={{ maxLength: 30 }}
                helperText={`${preferredName.length}/30`}
                className={styles.formField}
              />
            </Grid>
            <Grid item xs={12}>                
              <TextField
                label="Status Message"
                value={statusMessage}
                onChange={(e) => setStatusMessage(e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
                placeholder="What are you working on?"
                multiline
                rows={2}
                inputProps={{ maxLength: 100 }}
                helperText={`${statusMessage.length}/100`}
                className={styles.formField}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={cityTimezoneOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  return `${option.city}, ${option.country}`;
                }}
                value={city ? (cityTimezoneOptions.find(option => 
                  city.startsWith(`${option.city}, ${option.country}`)) || city) : null}
                onChange={handleCityChange}
                onInputChange={(event, newInputValue) => {
                  if (event) {
                    setCity(newInputValue);
                  }
                }}
                renderInput={(params) =>                    
                    <TextField 
                      {...params} 
                      label="Location" 
                      variant="outlined" 
                      margin="dense" 
                      placeholder="Enter City, Country"
                      helperText={`City, Country ${city.length}/50`}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            <Tooltip title="Select a location or enter manually">
                              <InfoIcon color="action" fontSize="small" style={{ marginLeft: 8 }} className={styles.infoIconNormal}/>
                            </Tooltip>
                          </>
                        )
                        
                      }}
                      className={styles.formField}
                    />
                  
                }
                freeSolo
                isOptionEqualToValue={(option, value) => option.city === value.city}
                noOptionsText="No cities found"
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={availableTimezones}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  return option.label;
                }}
                value={timezone ? (availableTimezones.find(option => option.value === timezone) || null) : null}
                onChange={handleTimezoneChange}
                disableClearable
                selectOnFocus
                handleHomeEndKeys
                freeSolo={false}
                renderInput={(params) => 
                  <TextField 
                    {...params} 
                    label="Timezone" 
                    variant="outlined" 
                    margin="dense" 
                    placeholder="Search and select timezone"
                    helperText="Select your timezone from the list"
                    inputProps={{
                      ...params.inputProps,
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <Tooltip title="Select your timezone with GMT offset">
                            <InfoIcon color="action" fontSize="small" style={{ marginLeft: 8 }} className={styles.infoIconNormal} />
                          </Tooltip>
                        </>
                      )
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1d9f9f',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1d9f9f',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#1d9f9f',
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(0, 0, 0, 0.54)',
                        fontSize: '0.75rem',
                        marginLeft: '4px',
                      },
                      '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                        color: 'rgba(0, 0, 0, 0.54)',
                      },
                    }}
                  />
                }
                isOptionEqualToValue={(option, value) => option.value === value.value}
                noOptionsText="No timezones found"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Birthday"
                type="date"
                value={birthday}
                onChange={(e) => {
                  const newBirthday = e.target.value;
                  
                  // Check time restriction first
                  if (birthdayRestriction.restricted && newBirthday !== '') {
                    if (onNotification) {
                      onNotification(birthdayRestriction.message, 'warning');
                    }
                    return;
                  }
                  
                  // Check age validation
                  if (newBirthday !== '' && !isValidBirthdayAge(newBirthday)) {
                    const minAge = userProfile?.birthdayRestrictionConfig?.minAge || 14;
                    if (onNotification) {
                      onNotification(`You must be at least ${minAge} years old to set your birthday`, 'error');
                    }
                    return;
                  }
                  
                  setBirthday(newBirthday);
                }}
                fullWidth
                variant="outlined"
                margin="dense"
                inputProps={{
                  max: getMaxAllowedBirthDate() // Restrict calendar to valid dates
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: birthdayRestriction.restricted ? 'rgba(0, 0, 0, 0.23)' : '#1d9f9f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: birthdayRestriction.restricted ? 'rgba(0, 0, 0, 0.23)' : '#1d9f9f',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: birthdayRestriction.restricted ? 'rgba(0, 0, 0, 0.6)' : '#1d9f9f',
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(0, 0, 0, 0.54)',
                    fontSize: '0.75rem',
                    marginLeft: '4px',
                  },
                }}
                InputProps={{
                  readOnly: birthdayRestriction.restricted,
                  disabled: birthdayRestriction.restricted,
                  endAdornment: (
                    <div className={styles.birthdayFieldEndAdornment}>
                      {/* In restricted mode: show lockpad icon and delete icon */}
                      {userProfile?.birthdayRestrictionConfig && birthdayRestriction.restricted && (
                        <>
                          <Tooltip title="Contact your admin to have the restriction timer reset">
                            <LockClockIcon 
                              color="action" 
                              fontSize="small" 
                              className={styles.lockIconActive}
                            />
                          </Tooltip>
                          
                          {birthday !== '' && (
                            <Tooltip title={`Remove birthday (${userProfile.birthdayRestrictionConfig.value} ${userProfile.birthdayRestrictionConfig.unit} restriction will still apply)`}>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  setBirthday('');
                                }}
                                className={styles.deleteIconButton}
                                aria-label="Delete birthday"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      
                      {/* In non-restricted mode: show info icon and delete icon */}
                      {(!userProfile?.birthdayRestrictionConfig || !birthdayRestriction.restricted) && (
                        <>
                          <Tooltip title="Your birth year will remain private">
                            <InfoIcon 
                              color="action" 
                              fontSize="small"
                              className={styles.infoIconNormal}
                            />
                          </Tooltip>
                          
                          {/* Show delete button when birthday is set */}
                          {birthday !== '' && (
                            <Tooltip title="Remove birthday">
                              <IconButton 
                                size="small" 
                                onClick={() => setBirthday('')}
                                className={styles.deleteIconButton}
                                aria-label="Delete birthday"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </div>
                  )
                }}
                helperText={
                  <span>
                    You must be at least {userProfile?.birthdayRestrictionConfig?.minAge || 14} years old. 
                    Birthday can be updated only every {userProfile?.birthdayRestrictionConfig?.value || 30} {userProfile?.birthdayRestrictionConfig?.unit || 'days'}.
                    {birthdayRestriction.restricted && birthdayRestriction.timeRemaining && (
                      <> Time remaining: {birthdayRestriction.timeRemaining}.</>
                    )}
                  </span>
                }
                FormHelperTextProps={{
                  className: styles.formHelperText
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                This information may be visible to other users. Your birth year will remain private.
              </Typography>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <div className={styles.footerDivider}></div>
      <DialogActions className={styles.dialogActions}>
        <button 
          onClick={handleCancel}
          className={styles.cancelButton}
        >
          {(() => {
            const buttonText = hasChanges ? 'Discard' : (hasSaved ? 'Close' : 'Cancel');
            logger.debug('Button text:', buttonText, '| hasChanges:', hasChanges, '| hasSaved:', hasSaved);
            return buttonText;
          })()}
        </button>
        <button
          onClick={handleSave}
          disabled={isUploading || !hasChanges}
          title={!hasChanges ? 'No changes to save' : ''}
          className={`${styles.saveButton} ${(isUploading || !hasChanges) ? styles.saveButtonDisabled : ''}`}
        >
          {isUploading ? 'Saving...' : 'Save Profile'}
        </button>
      </DialogActions>
      
      {!onNotification && (
        <Snackbar 
          open={snackbarOpen} 
          autoHideDuration={4000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbarSeverity} 
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      )}
      <PhotoGallery
        open={photoGalleryOpen}
        onClose={handleClosePhotoGallery}
        onPhotoSelected={handlePhotoSelected}
        onNotification={onNotification}
        userProfile={userProfile}
      />
    </Dialog>
  );
};

export default EditUserProfile;
